/**
 * learning-cms-api.ts — API client for the admin Learning CMS endpoints.
 *
 * All endpoints route through the shared REST gateway using the per-service
 * /v1/engagement/admin/ path (Epic 12.2 — gateway strips /v1/engagement/admin ->
 * /admin; backend controllers stay at admin/v1/... unchanged).
 *   POST /v1/engagement/admin/v1/lessons                      — create lesson
 *   POST /v1/engagement/admin/v1/lessons/:lessonId/checks     — create knowledge check
 *   PUT  /v1/engagement/admin/v1/checks/:checkId              — edit knowledge check
 *   POST /v1/engagement/admin/v1/checks/:checkId/approve      — approve knowledge check
 *   POST /v1/engagement/admin/v1/lessons/:lessonId/check-opt-in — toggle check opt-in
 *   POST /v1/engagement/admin/v1/ai/draft-check               — AI-draft a knowledge check
 *
 * @NonPhiEndpoint — lesson/check content is clinical corpus, not member PHI.
 *
 * Auth: staff JWT attached via Authorization header (Cognito OIDC).
 * Token is obtained from the auth context if available.
 */

import { SERVICES_BASE_URL } from '../../lib/services-base.js';

import type { TaxonomyConditionOption } from './lesson-authoring-form.js';

// ─── API helper ───────────────────────────────────────────────────────────────

async function apiFetch(path: string, options?: RequestInit, token?: string): Promise<Response> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options?.headers ?? {}),
  };
  return fetch(`${SERVICES_BASE_URL}${path}`, {
    ...options,
    headers,
  });
}

async function parseRfc9457Error(response: Response): Promise<Error> {
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/problem+json')) {
    try {
      const problem = (await response.json()) as { title?: string; detail?: string };
      return new Error(problem.detail ?? problem.title ?? `HTTP ${String(response.status)}`);
    } catch {
      // fall through
    }
  }
  return new Error(`Request failed with status ${String(response.status)}`);
}

// ─── Request/response types ──────────────────────────────────────────────────

export interface CreateLessonBody {
  title: string;
  clinicalMessage: string;
  contentType: string;
  format: string;
  languageCode: string;
  conditionTags: string[];
  oapMeasureBindings: string[];
  phase?: number | null;
}

export interface CreateLessonResponse {
  lessonId: string;
}

export interface CreateCheckBody {
  questions: unknown[];
  language: string;
  origin: string;
}

export interface CreateCheckResponse {
  checkId: string;
}

export interface EditCheckBody {
  questions?: unknown[];
  language?: string;
  diffSummary?: string;
}

export interface EditCheckResponse {
  checkId: string;
}

export interface ApproveCheckResponse {
  checkId: string;
  status: string;
}

export interface ToggleCheckOptInResponse {
  lessonId: string;
  checkAttached: boolean;
}

export interface DraftCheckBody {
  /** Optional — the lesson may not be saved yet when the author requests an AI draft. */
  lessonId?: string;
  lessonContext: string;
  languageCode: string;
}

export interface DraftCheckResponse {
  questions: unknown[];
  checkWorthySuggestion: boolean;
  language: 'en' | 'es';
  origin: 'ai-drafted';
}

// ─── API functions ────────────────────────────────────────────────────────────

export const learningCmsApi = {
  /** Load the live condition taxonomy (4.1a's GET /v1/engagement/admin/v1/taxonomy/conditions) so the
   *  lesson form can populate its condition + OAP-measure vocabularies. */
  async listConditions(token?: string): Promise<TaxonomyConditionOption[]> {
    const response = await apiFetch(
      '/v1/engagement/admin/v1/taxonomy/conditions',
      { method: 'GET' },
      token,
    );
    if (!response.ok) {
      throw await parseRfc9457Error(response);
    }
    const data = (await response.json()) as { items: TaxonomyConditionOption[] };
    return data.items;
  },

  async createLesson(data: CreateLessonBody, token?: string): Promise<CreateLessonResponse> {
    const response = await apiFetch(
      '/v1/engagement/admin/v1/lessons',
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      token,
    );
    if (!response.ok) {
      throw await parseRfc9457Error(response);
    }
    return (await response.json()) as CreateLessonResponse;
  },

  async saveCheck(
    lessonId: string,
    data: CreateCheckBody,
    token?: string,
  ): Promise<CreateCheckResponse> {
    const response = await apiFetch(
      `/v1/engagement/admin/v1/lessons/${lessonId}/checks`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      token,
    );
    if (!response.ok) {
      throw await parseRfc9457Error(response);
    }
    return (await response.json()) as CreateCheckResponse;
  },

  async editCheck(
    checkId: string,
    data: EditCheckBody,
    token?: string,
  ): Promise<EditCheckResponse> {
    const response = await apiFetch(
      `/v1/engagement/admin/v1/checks/${checkId}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
      token,
    );
    if (!response.ok) {
      throw await parseRfc9457Error(response);
    }
    return (await response.json()) as EditCheckResponse;
  },

  async approveCheck(checkId: string, token?: string): Promise<ApproveCheckResponse> {
    const response = await apiFetch(
      `/v1/engagement/admin/v1/checks/${checkId}/approve`,
      { method: 'POST' },
      token,
    );
    if (!response.ok) {
      throw await parseRfc9457Error(response);
    }
    return (await response.json()) as ApproveCheckResponse;
  },

  async toggleCheckOptIn(
    lessonId: string,
    checkAttached: boolean,
    token?: string,
  ): Promise<ToggleCheckOptInResponse> {
    const response = await apiFetch(
      `/v1/engagement/admin/v1/lessons/${lessonId}/check-opt-in`,
      {
        method: 'POST',
        body: JSON.stringify({ checkAttached }),
      },
      token,
    );
    if (!response.ok) {
      throw await parseRfc9457Error(response);
    }
    return (await response.json()) as ToggleCheckOptInResponse;
  },

  async draftCheck(
    lessonId: string | null,
    lessonContext: string,
    languageCode: string,
    token?: string,
  ): Promise<DraftCheckResponse> {
    const body: DraftCheckBody = {
      lessonContext,
      languageCode,
      ...(lessonId !== null ? { lessonId } : {}),
    };
    const response = await apiFetch(
      '/v1/engagement/admin/v1/ai/draft-check',
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
      token,
    );
    if (!response.ok) {
      throw await parseRfc9457Error(response);
    }
    return (await response.json()) as DraftCheckResponse;
  },
};
